import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import { DURATION, EASE_OUT_EXPO } from './motion';

export interface AppNotification {
  id: string;
  kind: string;
  title: string;
  body: string;
  href: string | null;
  jobId: string | null;
  tripId: string | null;
  readAt: string | null;
  createdAt: string;
}

interface ToastItem {
  id: string;
  title: string;
  body: string;
  href: string | null;
  tone: 'success' | 'error' | 'info';
  icon: string;
}

interface NotificationsContextValue {
  items: AppNotification[];
  unreadCount: number;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const reduced = useReducedMotion();
  const seenRef = useRef<Set<string>>(new Set());
  const primedRef = useRef(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const enabled = status === 'authenticated';

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () =>
      api.get<{ items: AppNotification[]; unreadCount: number }>('/notifications', {
        limit: 20,
      }),
    enabled,
    refetchInterval: enabled ? 8_000 : false,
  });

  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;

  useEffect(() => {
    if (!enabled) {
      primedRef.current = false;
      seenRef.current = new Set();
      return;
    }
    if (!data) return;

    if (!primedRef.current) {
      for (const n of items) seenRef.current.add(n.id);
      primedRef.current = true;
      return;
    }

    const fresh = items.filter((n) => !n.readAt && !seenRef.current.has(n.id));
    for (const n of fresh) {
      seenRef.current.add(n.id);
      setToasts((prev) =>
        [
          {
            id: n.id,
            title: n.title,
            body: n.body,
            href: n.href,
            ...toastLook(n.kind),
          },
          ...prev,
        ].slice(0, 4),
      );
      void queryClient.invalidateQueries({ queryKey: ['trips'] });
      if (n.tripId) void queryClient.invalidateQueries({ queryKey: ['trip', n.tripId] });
    }
  }, [data, enabled, items, queryClient]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const markRead = useCallback(
    async (id: string) => {
      await api.post(`/notifications/${id}/read`);
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    [queryClient],
  );

  const markAllRead = useCallback(async () => {
    await api.post('/notifications/read-all');
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }, [queryClient]);

  const value = useMemo(
    () => ({ items, unreadCount, markRead, markAllRead }),
    [items, unreadCount, markRead, markAllRead],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      {/*
        Four toasts at once are taller than a phone, and the stack grows upward
        from a fixed bottom edge — so without a ceiling the newest one can end
        up above the top of the screen, where nothing can scroll to it. The cap
        clips the oldest instead, which is the one already read. The bottom
        inset also clears the home indicator on a phone in fullscreen.
      */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex max-h-[calc(100dvh-4rem)] flex-col gap-2 overflow-hidden px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] pt-8 sm:left-auto sm:w-[25rem] sm:px-4 sm:pb-4">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={reduced ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: DURATION.base, ease: EASE_OUT_EXPO }}
              /* The edge and the icon's disc take the tone, so a delay reads as
                 one before it is read. */
              className={`pointer-events-auto rounded-lg border bg-[var(--surface-overlay)] py-3.5 pl-3.5 pr-2 shadow-[var(--shadow-overlay)] ${TOAST_EDGE[toast.tone]}`}
            >
              <div className="flex items-start gap-3">
                <span
                  aria-hidden
                  className={`mt-0.5 grid size-8 shrink-0 place-items-center rounded-full text-base ${TOAST_ICON_BG[toast.tone]}`}
                >
                  {toast.icon}
                </span>
                {/* min-w-0: a flex item refuses to shrink below its content by
                    default, so one long word in a trip title pushed the close
                    button off the card. */}
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="break-words text-sm font-semibold leading-snug tracking-tight">
                    {toast.title}
                  </div>
                  <p className="mt-1 break-words text-[0.8rem] leading-relaxed text-[var(--text-muted)]">
                    {toast.body}
                  </p>
                  {toast.href ? (
                    <Link
                      to={toast.href}
                      className="mt-2 inline-block text-sm font-medium text-clay-600 hover:underline dark:text-clay-300"
                      onClick={() => {
                        dismissToast(toast.id);
                        void markRead(toast.id);
                      }}
                    >
                      Открыть →
                    </Link>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="grid size-8 shrink-0 place-items-center rounded-full text-lg leading-none text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-strong)]"
                  onClick={() => dismissToast(toast.id)}
                  aria-label="Закрыть"
                >
                  ×
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </NotificationsContext.Provider>
  );
}

const TOAST_EDGE: Record<ToastItem['tone'], string> = {
  success: 'border-[var(--border-overlay)]',
  info: 'border-sun-400/50',
  error: 'border-red-500/50',
};

const TOAST_ICON_BG: Record<ToastItem['tone'], string> = {
  success: 'bg-lagoon-500/15',
  info: 'bg-sun-400/20',
  error: 'bg-red-500/15',
};

/** How a notification looks as a toast: a delay or a failure is an alert, a gate is news. */
function toastLook(kind: string): Pick<ToastItem, 'tone' | 'icon'> {
  if (kind === 'flight_alert') return { tone: 'error', icon: '✈️' };
  if (kind.startsWith('flight')) return { tone: 'info', icon: '✈️' };
  if (kind.endsWith('_failed')) return { tone: 'error', icon: '⚠️' };
  return { tone: 'success', icon: '✨' };
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationsProvider');
  return ctx;
}
